import { Fragment, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, IconButton, validationUtils } from 'basic-components';
import { Clear, Info } from 'basic-components/icons';

import { PickersUtils } from '../Utils';
import Modal from './Modal';

import { Else, If, Then, When } from 'react-if';

import classNames from 'classnames';
import '../Picker.scss';

const EMPTY_ARRAY = [];
const EMPTY_OBJ = {};
export const SymbolPicker = ({
  disabled = false,
  size = 'normal',
  tooltipProps = EMPTY_OBJ,
  optionalText = undefined,
  onChange,
  name,
  defaultValue = undefined,
  validators = EMPTY_ARRAY,
  required = false,
  noError = false,
  ref
}) => { 

  const [isOpen, open] = useState(false),
        [value, setValue] = useState(),
        [isValid, setValid] = useState(true),
        [errorMessage, setErrorMessage] = useState(undefined),
        [isOverflowed, setOverflowed] = useState(false),

        refErrorMessage = useRef(null),

        openModal = useCallback(() => open(true), [open]),
        closeModal = useCallback(() => open(false), [open]),
        onChangeInner = useCallback(value => setValue(value), []),
        clearValue = useCallback(() => !disabled && setValue(""), [disabled]),
        validate = useCallback(v => validationUtils.validateNoState(v, validators, name, required, setValid, setErrorMessage), [name, required, validators])
        
  useEffect(() => {
    setValue(defaultValue)
  }, [defaultValue])
  
  useEffect(() => {
    value !== undefined && onChange(value, name)
  }, [value, name, onChange])

  useEffect(() => {
    validate(typeof value === 'object' ? value.groupName : value)
  }, [value, validate])

  useImperativeHandle(ref, () => ({ 
    validateForForm: () => validate(typeof value === 'object' ? value.groupName : value),
    isValid: () => isValid
  }))
  useEffect(() => {
    if(refErrorMessage.current && refErrorMessage.current.clientWidth < refErrorMessage.current.scrollWidth) {
      setOverflowed(true)
    } else {
      setOverflowed(false)
    }
  }, [isValid, errorMessage])

  return (
    <div className="ng-common-picker">
      <div className={classNames(
        "ng-common-picker__row",  
        { "ng-common-picker__row--choosen": value !== "" && value !== undefined && value !== " " }, 
        { "ng-common-picker__row--not-choosen": value === "" || value === undefined || value === " " },
        { "ng-common-picker__row--no-error": noError }
      )}>
        <If condition={value !== "" && value !== undefined && value !== " "}>
          <Then>
            {() => (
              <Fragment>
                <div className="ng-common-picker__row--choosen__top">
                  <p
                      className="ng-common-picker__row--choosen__top__name" 
                      data-testid="symbol-name"
                  >{Object.values(value?.symbols || {}).join(", ")}</p>
                  <IconButton
                      className={classNames(
                        "ng-common-picker__row--choosen__top__clear",
                        { "ng-common-picker__row--choosen__top__clear--is-disabled": disabled }
                      )}
                      onClick={clearValue}
                  >
                    <Clear/>
                  </IconButton>
                </div>
                <p className="ng-common-picker__row--choosen__optional">{"Group: " + (value?.groupName || "Unknown")}</p>
              </Fragment>
            )}
          </Then>
          <Else>
            {() => (
              <Button
                  disabled={disabled}
                  onClick={openModal}
                  size={size}
                  type="button"
                  variant="text"
              >
                Select symbol
              </Button>
            )}
          </Else>
        </If>
        <div 
            className={classNames("ng-common-picker__error-container", { "ng-common-picker__error-container--is-none": isValid })}
            data-testid="text-field-error-container"
        >
          <p ref={refErrorMessage}>
            {errorMessage}
          </p>
          <When condition={isOverflowed}>
            {() => PickersUtils.getErrorTooltip(errorMessage, "ng-common-picker__error-container__more")}
          </When>
        </div>
      </div>
      <When condition={Object.keys(tooltipProps || {})?.length > 0}>
        {() => (
          <div 
              className="ng-common-picker__tooltip-container"
              data-place="left"
              {...tooltipProps}
          >
            <Info/>
          </div>
        )}
      </When>
      <When condition={isOpen}>
        {() => (
          <Modal
              closeModal={closeModal}
              defaultValue={defaultValue}
              onChange={onChangeInner}
              optionalText={optionalText}
          />
        )}
      </When>
    </div>
  )
}


SymbolPicker.propTypes = {
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  disabled: PropTypes.bool,
  name: PropTypes.string.isRequired,
  noError: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  optionalText: PropTypes.string,
  required: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'normal', 'large']),
  tooltipProps: PropTypes.object,
  validators: PropTypes.arrayOf(PropTypes.shape({
    message: PropTypes.string.isRequired,
    validator: PropTypes.func.isRequired
  })),
}

SymbolPicker.displayName = 'SymbolPicker'
